import { ChevronRight, Package } from "@/components/icons";
import { Badge, Button } from "@/components/kit";
import { cn } from "@/lib/utils";
import type { ImportOrderPhase13ListItemDTO } from "@/modules/inventory/domain/types";
import { formatDateLocal, formatDateTimeLocal } from "@/shared/lib/date-time";
import {
  formatLakAmount,
  IMPORT_ORDER_SOURCE_CLASS,
  IMPORT_ORDER_SOURCE_LABELS,
  IMPORT_ORDER_STATUS_CLASS,
  IMPORT_ORDER_STATUS_LABELS,
} from "../lib/import-order-labels";
import { ImportOrderReceiveProgress } from "./ImportOrderReceiveProgress";
import { ImportOrderPendingCountBadge } from "./ImportOrderPendingBadge";

type ImportOrderListCardProps = {
  row: ImportOrderPhase13ListItemDTO;
  canReceive: boolean;
  onOpen: () => void;
  onReceive: () => void;
};

export function ImportOrderListCard({
  row,
  canReceive,
  onOpen,
  onReceive,
}: ImportOrderListCardProps) {
  const hasProducts = row.linePreviewItems.length > 0;

  return (
    <article
      className={cn(
        "brand-card overflow-hidden transition-colors hover:border-secondary/45",
        row.isOverdue && "border-destructive/30 bg-destructive/2",
      )}
    >
      <div className="flex min-h-full">
        <div
          className={cn(
            "w-1 shrink-0",
            row.isOverdue ? "bg-destructive/70" : "bg-secondary/60",
          )}
          aria-hidden
        />

        <div className="flex min-w-0 flex-1 flex-col gap-3 p-4 lg:flex-row lg:items-stretch lg:gap-4">
          <button
            type="button"
            onClick={onOpen}
            className="flex min-w-0 flex-1 flex-col gap-3 text-left transition-opacity hover:opacity-90"
          >
            <div className="flex flex-wrap items-start gap-2">
              <Badge
                variant="outline"
                className={IMPORT_ORDER_SOURCE_CLASS[row.source]}
              >
                {IMPORT_ORDER_SOURCE_LABELS[row.source]}
              </Badge>
              <Badge className={IMPORT_ORDER_STATUS_CLASS[row.status]}>
                {IMPORT_ORDER_STATUS_LABELS[row.status]}
              </Badge>
              {row.isOverdue ? (
                <Badge variant="destructive">ເກີນກຳນົດ</Badge>
              ) : null}
              <ImportOrderPendingCountBadge count={row.pendingLineCount ?? 0} />
            </div>

            <div className="space-y-1">
              {hasProducts ? (
                <ul className="space-y-0.5">
                  {row.linePreviewItems.map((item) => (
                    <li
                      key={item}
                      className="flex items-start gap-2 font-medium text-sm leading-snug sm:text-base"
                    >
                      <Package className="mt-0.5 size-3.5 shrink-0 text-secondary" />
                      <span className="min-w-0">{item}</span>
                    </li>
                  ))}
                  {row.linePreviewMoreCount > 0 ? (
                    <li className="pl-5 text-muted-foreground text-xs">
                      +{row.linePreviewMoreCount} ກຸ່ມອື່ນ
                    </li>
                  ) : null}
                </ul>
              ) : (
                <p className="font-medium text-muted-foreground text-sm">
                  ຍັງບໍ່ມີລາຍການສິນຄ້າ
                </p>
              )}
              <p className="text-muted-foreground text-xs">
                {row.lineCount > 0
                  ? `${row.linePreviewItems.length + row.linePreviewMoreCount} ກຸ່ມສິນຄ້າ · ${row.totalOrdered} ຊິ້ນສັ່ງ`
                  : "ຮ່າງ — ຍັງບໍ່ໄດ້ເພີ່ມສິນຄ້າ"}
              </p>
            </div>

            <div className="grid gap-2 sm:grid-cols-2">
              <div className="rounded-xl border border-border/60 bg-muted/30 px-3 py-2">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wide">
                  ເລກສັ່ງແພລດຟອร์ມ
                </p>
                <p className="mt-0.5 break-all font-medium text-sm tabular-nums">
                  {row.externalOrderNo}
                </p>
              </div>
              <div className="rounded-xl border border-border/60 bg-muted/30 px-3 py-2">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wide">
                  ເລກອ້າງອີງຮ້ານ
                </p>
                <p className="mt-0.5 break-all font-medium text-sm tabular-nums">
                  {row.orderNumber}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-x-4 gap-y-1 text-muted-foreground text-xs">
              <span>ສ້າງ {formatDateTimeLocal(row.createdAt)}</span>
              {row.orderedAt ? (
                <span>ສັ່ງ {formatDateLocal(row.orderedAt)}</span>
              ) : null}
              {row.expectedArrivalDate ? (
                <span
                  className={cn(
                    row.isOverdue && "font-medium text-destructive",
                  )}
                >
                  ຄາດຮອດ {formatDateLocal(row.expectedArrivalDate)}
                </span>
              ) : null}
            </div>
          </button>

          <div className="flex shrink-0 flex-col gap-3 border-border/50 lg:w-56 lg:border-l lg:pl-4">
            <ImportOrderReceiveProgress
              received={row.totalReceived}
              ordered={row.totalOrdered}
            />

            <div className="space-y-1 text-sm">
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-muted-foreground text-xs">Landed</span>
                <span className="font-semibold text-secondary tabular-nums">
                  {formatLakAmount(row.landedTotalLak)}
                </span>
              </div>
              {row.totalRemaining > 0 ? (
                <div className="flex items-baseline justify-between gap-2 text-xs">
                  <span className="text-muted-foreground">ຄ້າງຮັບ</span>
                  <span className="tabular-nums">
                    {row.totalRemaining} ·{" "}
                    {formatLakAmount(row.inTransitValueLak)}
                  </span>
                </div>
              ) : null}
            </div>

            <div className="mt-auto flex flex-col gap-2 sm:flex-row lg:flex-col">
              {canReceive ? (
                <Button
                  size="sm"
                  className="w-full rounded-full"
                  onClick={(event) => {
                    event.stopPropagation();
                    onReceive();
                  }}
                >
                  ຮັບເຂົ້າ
                </Button>
              ) : null}
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="w-full rounded-full"
                onClick={onOpen}
              >
                ເບິ່ງລາຍລະອຽດ
                <ChevronRight data-icon="inline-end" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}
